import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotifications, MarkdownWithWidgets } from '@byscott-io/corebyscott';
import { recipes, recipeCategories, ingredients as ingredientsApi } from '../api/client';
import RecipeForm from '../components/recipes/RecipeForm';
import IngredientList from '../components/recipes/IngredientList';
import Modal from '../components/common/Modal';

export default function RecipePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notifications = useNotifications();
  const [recipe, setRecipe] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [recipeRes, categoriesRes, historyRes] = await Promise.all([
        recipes.get(id),
        recipeCategories.list(),
        recipes.history(id),
      ]);
      setRecipe(recipeRes.data.recipe);
      setCategories(categoriesRes.data.categories);
      setHistory(historyRes.data);
    } catch {
      notifications.error('Failed to load recipe');
      navigate('/app/recipes');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRecipe = async (data) => {
    try {
      const response = await recipes.update(id, { recipe: data, category_ids: data.category_ids });
      setRecipe(response.data.recipe);
      notifications.success('Recipe updated!');
      setShowEditModal(false);
    } catch (err) {
      notifications.error(err.response?.data?.errors?.join(', ') || 'Failed to update recipe');
    }
  };

  const handleDeleteRecipe = async () => {
    if (!confirm('Are you sure you want to delete this recipe?')) return;
    try {
      await recipes.delete(id);
      notifications.success('Recipe deleted');
      navigate('/app/recipes');
    } catch {
      notifications.error('Failed to delete recipe');
    }
  };

  const handleAddIngredient = async (data) => {
    try {
      await ingredientsApi.create(id, { ingredient: data });
      loadData();
      notifications.success('Ingredient added');
    } catch {
      notifications.error('Failed to add ingredient');
    }
  };

  const handleUpdateIngredient = async (ingredientId, data) => {
    try {
      await ingredientsApi.update(id, ingredientId, { ingredient: data });
      loadData();
    } catch {
      notifications.error('Failed to update ingredient');
    }
  };

  const handleDeleteIngredient = async (ingredientId) => {
    try {
      await ingredientsApi.delete(id, ingredientId);
      loadData();
    } catch {
      notifications.error('Failed to delete ingredient');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!recipe) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={() => navigate('/app/recipes')}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2 inline-flex items-center"
          >
            ← Back to Recipes
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{recipe.title}</h1>
          {recipe.categories?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {recipe.categories.map((cat) => (
                <span
                  key={cat.id}
                  className="px-3 py-1 text-sm rounded-full"
                  style={{ backgroundColor: cat.color + '20', color: cat.color }}
                >
                  {cat.name}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowEditModal(true)}
            className="px-4 py-2 text-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
          >
            Edit
          </button>
          <button
            onClick={handleDeleteRecipe}
            className="px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Meta Info */}
      <div className="flex flex-wrap gap-6 text-gray-600 dark:text-gray-400">
        {recipe.prep_time_minutes > 0 && (
          <div>
            <span className="text-gray-500 dark:text-gray-500">Prep:</span>{' '}
            {recipe.prep_time_minutes} min
          </div>
        )}
        {recipe.cook_time_minutes > 0 && (
          <div>
            <span className="text-gray-500 dark:text-gray-500">Cook:</span>{' '}
            {recipe.cook_time_minutes} min
          </div>
        )}
        {recipe.total_time_minutes > 0 && (
          <div className="font-medium">
            <span className="text-gray-500 dark:text-gray-500">Total:</span>{' '}
            {recipe.total_time_minutes} min
          </div>
        )}
        {recipe.servings && (
          <div>
            <span className="text-gray-500 dark:text-gray-500">Servings:</span>{' '}
            {recipe.servings}
          </div>
        )}
        {recipe.is_public && (
          <span className="text-green-600 dark:text-green-400">🌐 Public</span>
        )}
      </div>

      {/* History */}
      {history && history.last_made_at && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-blue-700 dark:text-blue-300">
          Last made: {formatDate(history.last_made_at)}{' '}
          ({history.days_since_last_made === 0 ? 'today' : `${history.days_since_last_made} days ago`})
        </div>
      )}

      {/* Description */}
      {recipe.description && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <p className="text-gray-700 dark:text-gray-300">{recipe.description}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ingredients */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Ingredients
            </h2>
            <IngredientList
              ingredients={recipe.ingredients || []}
              onAdd={handleAddIngredient}
              onUpdate={handleUpdateIngredient}
              onDelete={handleDeleteIngredient}
            />
          </div>
        </div>

        {/* Instructions */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Instructions
            </h2>
            {recipe.instructions ? (
              <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
                <MarkdownWithWidgets source={recipe.instructions} />
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No instructions added yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* Notes */}
      {recipe.notes && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
            Notes
          </h2>
          <div className="prose dark:prose-invert max-w-none text-yellow-700 dark:text-yellow-300">
            <MarkdownWithWidgets source={recipe.notes} />
          </div>
        </div>
      )}

      {/* Source */}
      {recipe.source_url && (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Source:{' '}
          <a
            href={recipe.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 hover:underline"
          >
            {recipe.source_url}
          </a>
        </div>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Recipe"
        size="lg"
      >
        <RecipeForm
          recipe={recipe}
          categories={categories}
          onSubmit={handleUpdateRecipe}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>
    </div>
  );
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
